'use client';

import { autoComplete } from '@/types/html';
import { ChangeEvent, useCallback, useState } from 'react';
import cn from 'classnames';
import { HTMLGoogleAnalyticsData } from '@/types/globals';
import {
	characterCountLimitClassNames,
	contextRowClassNames,
	fieldSetClassNames,
	helperTextClassNames,
	inputClassNames,
	labelClassNames,
} from '../styles';
import { ErrorTip } from '../ErrorTip';

export interface InputEmailProps extends HTMLGoogleAnalyticsData {
	autoComplete?: autoComplete;
	helperText?: string;
	id: string;
	label: string;
	maxLength?: number;
	minLength?: number;
	name: string;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	pattern?: RegExp;
	required?: boolean;
	disabled?: boolean;
	testId?: string;
	value?: string;
	fieldsetClassName?: string;
	fieldsetProps?: Omit<React.HTMLAttributes<HTMLFieldSetElement>, 'className'>;
	inputClassName?: string;
	inputProps?: Omit<
		React.HTMLAttributes<HTMLInputElement>,
		| 'value'
		| 'onChange'
		| 'onBlur'
		| 'minLength'
		| 'maxLength'
		| 'id'
		| 'name'
		| 'placeholder'
		| 'type'
		| 'autoComplete'
		| 'required'
		| 'pattern'
		| 'disabled'
		| 'className'
	>;
	labelClassName?: string;
	labelProps?: Omit<
		React.HTMLAttributes<HTMLLabelElement>,
		'htmlFor' | 'className'
	>;
	contextRowClassName?: string;
	contextRowProps?: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>;
	error?: string;
}

const InputEmail = (props: InputEmailProps) => {
	const {
		testId = 'InputText',
		autoComplete = 'off',
		id,
		label,
		maxLength,
		minLength,
		name,
		onBlur,
		onChange,
		disabled = false,
		pattern,
		helperText,
		required = false,
		value,
		fieldsetProps,
		inputProps,
		labelProps,
		contextRowProps,
		fieldsetClassName,
		labelClassName,
		inputClassName,
		contextRowClassName,
		error,
		...gaData
	} = props;
	const [characterCount, setCharacterCount] = useState(0);

	const stringifiedPattern = pattern ? pattern.toString() : undefined;

	const manInTheMiddleOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setCharacterCount(e.target.value.length);

			if (onChange) {
				onChange(e);
			}
		},
		[onChange],
	);

	return (
		<fieldset
			{...fieldsetProps}
			className={cn(fieldsetClassName, fieldSetClassNames)}
			data-testid={testId}
			{...gaData}
		>
			<input
				{...inputProps}
				className={cn(inputClassName, inputClassNames, {
					'!border-pink-600': error,
				})}
				autoComplete={autoComplete}
				id={id}
				name={name}
				onBlur={onBlur}
				onChange={manInTheMiddleOnChange}
				placeholder={' '}
				type="email"
				value={value}
				disabled={disabled}
				required={required}
				aria-required={required}
				pattern={stringifiedPattern}
				minLength={minLength}
				maxLength={maxLength}
				aria-describedby={`${id}-error-tool-tip`}
			/>
			<label
				{...labelProps}
				htmlFor={id}
				className={cn(labelClassName, labelClassNames)}
			>
				{label}
			</label>
			{(helperText || maxLength) && (
				<div
					{...contextRowProps}
					className={cn(contextRowClassName, contextRowClassNames)}
				>
					<p
						className={cn(helperTextClassNames, {
							hidden: !helperText,
						})}
					>
						{helperText}
					</p>
					<p
						className={cn(characterCountLimitClassNames, {
							'ml-auto': !helperText,
							hidden: !maxLength,
						})}
					>
						{characterCount}/{maxLength}
					</p>
				</div>
			)}
			{!!error && (
				<div role="alert" className="sr-only">
					One or more fields are invalid. Please review the form.
				</div>
			)}
			<ErrorTip id={`${id}-error-tool-tip`} error={error} />
		</fieldset>
	);
};

export default InputEmail;
